import { userInfoStore } from '../../../store/userInfoStore';
import { fishingDrawerService } from './gameDrawerService';
import {
  DURATION_FROM_FISH,
  fishingCountdownDeductionValues,
  SHARK_TYPE,
} from '../fishingCore/constants';
import { fishingBetsStore } from '../fishingStore/betStore';
import { fishingGameConnection } from '../fishingConnection';
import { DefaultQueryParams } from '../../../core/constants/defaultQueryParams';
import { parseQuery } from '../../../core/helpers/parseQuery';
import { RoundStatus } from '../../../core/constants/roundStatus';
import { parseNumberByPrecision } from '../../../core/helpers/parseNumberByPrecision';
import { worker } from '../../../index';
import { checkHashStore } from '../../../store/checkHashStore';
import { betHistoryStore } from '../../../store/betHistoryStore';
import { BetStatus } from '../../../core/constants/betStatus';
import { liveUpdateStore } from '../../../store/liveUpdateStore';
import { THIS_GAMES_COLUMN_ID } from '../../../core/constants/liveBetsContants';
import { TranslationKeys } from '../../../core/constants/translationKeys';
import { notificationStore } from '../../../store/notificationStore';
import { soundService } from '../../../services/baseSoundService/soundService';
import { FishingSoundsName } from '../fishingCore/constants/sounds';
import { mainGameConnection } from '../../../core/mainGameConnection/mainGameConnection';
import { iframeService } from '../../../services/iframeService';
import { freeBetStore } from '../../../store/freeBetStore';

class FishingGameService {
  onGetInitialStateResult(data) {
    const { results, roundInfo, currentBets } = data;
    this.resetGameStore();
    if (!roundInfo) {
      setTimeout(() => {
        mainGameConnection.sendGameGetInitialState(false);
      }, 500);
    } else {
      const { onFinish, finished, started, open } = RoundStatus;
      const { roundId, status, duration, second, betsClosedDuration } = roundInfo;
      const currentRound = results.find((el) => el.roundId === roundId);
      checkHashStore.setNextRoundHash(currentRound?.resultHash);
      const lastRound = results.find((el) => el.status === finished);
      checkHashStore.setLastRoundInfo({
        salt: lastRound.resultSalt,
        hash: lastRound.resultHash,
        key: lastRound.resultKey,
      });
      checkHashStore.setLastRoundResult({
        id: lastRound.roundId,
        openedFishes: lastRound.roundData,
        startedDate: lastRound.startedDate,
      });
      const { [DefaultQueryParams.UserId]: userId } = parseQuery(window.location.search);
      const userBet = currentBets.find(
        (el) => el.userId === Number(userId) && el.status === BetStatus.Pending
      );
      const currentRoundInfo = results.find((el) => +el.roundId === Number(roundId));
      fishingBetsStore.setRoundDuration(duration);
      if (userBet) {
        const {
          amount,
          betData: { betStep },
        } = userBet;
        fishingBetsStore.setUserBet(userBet);
        fishingBetsStore.setIsBetAccepted(true);
        userInfoStore.setBetAmount(userBet.amount);
        if (currentRoundInfo.roundData) {
          const userBetSteps = currentRoundInfo.roundData.filter((el, index) => index >= betStep);
          const cashOutOdd = userBetSteps.reduce((acc, current) => current.odd * acc, 1);
          fishingBetsStore.setCashOutWin(
            parseNumberByPrecision(amount, cashOutOdd, userInfoStore.precision)
          );
          fishingBetsStore.setIsAllowCashOut(true);
        }
      }
      if ([onFinish, finished].includes(status)) {
        fishingBetsStore.setIsBetsClosed(true);
      } else {
        fishingBetsStore.setRoundId(roundId);
        fishingBetsStore.setOpenedFishes([...(currentRoundInfo.roundData || [])].reverse() || []);
        if (currentRoundInfo.roundData?.length) {
          fishingBetsStore.setIsRoundStart(true);
        }
        if (status === started) {
          this.sendWorker({
            timerValue: Math.round(second),
            ...fishingCountdownDeductionValues,
          });
        } else if (status === open) {
          const time = Math.abs(second) - betsClosedDuration;
          this.sendWorker({
            timerValue: Math.floor(DURATION_FROM_FISH - (time % DURATION_FROM_FISH)),
            ...fishingCountdownDeductionValues,
          });
        }
      }
    }
    checkHashStore.setNextHashTextInfo(TranslationKeys.OngoingRound);
    liveUpdateStore.setSelectedColumn(THIS_GAMES_COLUMN_ID);
  }

  handleCashOutResult({ winAmount, odd, userId: cashOutUserId, freeBetInfo }) {
    const { [DefaultQueryParams.UserId]: userId } = parseQuery(window.location.search);
    if (cashOutUserId === +userId) {
      if (freeBetInfo !== null) {
        freeBetStore.updateCurrentFreeBet(freeBetInfo);
      }
      soundService.play(FishingSoundsName.cashout);
      fishingBetsStore.setCashOutWin(null);
      fishingBetsStore.setIsAllowCashOut(false);
      fishingBetsStore.setWinAmount(winAmount);
      fishingBetsStore.setWinOdd(odd);
      iframeService.sendRoundFinished(winAmount || 0, userInfoStore.betAmount || 0);
      setTimeout(() => {
        this.resetGameStoreAfterCashOut();
      }, 1000);
    }
  }

  sendCashOut() {
    if (fishingBetsStore.isAllowCashOut) {
      fishingBetsStore.setIsAllowCashOut(false);
      const model = {
        roundId: fishingBetsStore.roundId,
        betId: fishingBetsStore.userBet.id,
      };
      fishingGameConnection.sendCashOut(model);
    }
  }

  handleBetsClosed() {
    // soundService.stop(FishingSoundsName.timer);
    fishingBetsStore.setIsBetsClosed(true);
  }

  handleRoundFin({ data: { finishedRound } }) {
    fishingBetsStore.setIsRoundStart(false);
    const { id, resultHash, resultKey, resultSalt, roundData } = finishedRound;
    checkHashStore.setLastRoundInfo({
      salt: resultSalt,
      hash: resultHash,
      key: resultKey,
    });
    checkHashStore.setNextRoundHash('');
    checkHashStore.setLastRoundResult({
      id,
      openedFishes: roundData,
    });
    if (fishingBetsStore.userBet) {
      betHistoryStore.getLastBet();
    }
    this.resetGameStoreAfterRoundFinished();
  }

  handleDoBetResult(data) {
    const { cryptoFreeBetInfo } = data;
    const { [DefaultQueryParams.UserId]: userId } = parseQuery(window.location.search);
    if (data.userId === Number(userId)) {
      if (cryptoFreeBetInfo !== null) {
        freeBetStore.updateCurrentFreeBet(cryptoFreeBetInfo);
      }
      iframeService.sendRoundStarted(userInfoStore.balance);
      fishingBetsStore.setUserBet(data);
      fishingBetsStore.setIsBetAccepted(true);
    }
  }

  sendBet() {
    if (fishingBetsStore.isBetsClosed) {
      return;
    }
    soundService.play(FishingSoundsName.bet);
    const { [DefaultQueryParams.GameTypeId]: gameTypeId } = parseQuery(window.location.search);
    const model = {
      roundId: fishingBetsStore.roundId,
      gameTypeId,
      amount: userInfoStore.betAmount,
      betData: {
        index: 0,
        betStep: fishingBetsStore.openedFishes.length,
        cashOutStep: 0,
      },
    };
    fishingBetsStore.setIsBetsClosed(true);
    mainGameConnection.doBet(model);
  }

  handleRoundStart({
    data: {
      startedRound: { roundId },
      results,
    },
  }) {
    const nextRound = results.find((el) => el.status === RoundStatus.started);
    // if (userSettingsStore.isSoundOn) {
    //   soundService.play(FishingSoundsName.timer);
    // }
    fishingBetsStore.setIsBetsClosed(false);
    fishingBetsStore.setRoundId(roundId);
    checkHashStore.setNextRoundHash(nextRound.resultHash);
    this.sendWorker({
      timerValue: fishingBetsStore.roundDuration,
      ...fishingCountdownDeductionValues,
    });
  }

  handleDoBetResultValidation(data) {
    const { [DefaultQueryParams.UserId]: userId } = parseQuery(window.location.search);
    fishingBetsStore.setIsBetsClosed(false);
    if (data.responseCode && +data.userId === +userId) {
      notificationStore.setNotification(data.responseCode); // success is 0
    }
  }

  handleBalanceUpdate(data) {
    userInfoStore.setUserBalance(data);
  }

  handleResultReport({ data: { resultReportData } }) {
    fishingBetsStore.setIsAllowCashOut(false);
    fishingBetsStore.setIsBetsClosed(true);
    if (!fishingBetsStore.isRoundStart) {
      fishingBetsStore.setIsRoundStart(true);
    }
    const lastFish = resultReportData[resultReportData.length - 1];
    if (lastFish.resultType === SHARK_TYPE) {
      fishingDrawerService.playSharkAnimation();
      soundService.play(FishingSoundsName.shark);
      setTimeout(() => {
        fishingDrawerService.resetSkeletonFromFishSpine();
        fishingDrawerService.playFishStaticAnimation();
        this.updateGameStoreAfterResultReport(lastFish, resultReportData);
      }, 800);
    } else {
      fishingDrawerService.playFishAnimation(lastFish.resultType);
      soundService.play(FishingSoundsName.catching);
      setTimeout(() => {
        fishingBetsStore.setFishOdd(lastFish.odd);
        fishingBetsStore.setOdd(lastFish.odd);
      }, 500);
      setTimeout(() => {
        fishingDrawerService.resetSkeletonFromFishSpine();
        fishingBetsStore.setFishOdd(null);
        fishingDrawerService.playFishStaticAnimation();
        this.updateGameStoreAfterResultReport(lastFish, resultReportData);
      }, 1500);
    }
  }

  updateGameStoreAfterResultReport(lastFish, resultReportData) {
    const { currentFreeBet } = freeBetStore;
    if (lastFish.resultType !== SHARK_TYPE) {
      fishingBetsStore.setIsBetsClosed(false);
      fishingBetsStore.updateOpenedFishes(lastFish);
      this.sendWorker({
        timerValue: fishingBetsStore.durationFromResultReport,
        ...fishingCountdownDeductionValues,
      });
    }
    if (fishingBetsStore.userBet && lastFish.resultType !== SHARK_TYPE) {
      const openedFishForUser = resultReportData.filter(
        (el, index) => index >= fishingBetsStore.userBet.betData.betStep
      );
      const odd = openedFishForUser.reduce(
        (acc, current) => parseNumberByPrecision(acc, current.odd, userInfoStore.precision, true),
        1
      );
      const cashOutWin = parseNumberByPrecision(
        odd,
        fishingBetsStore.userBet.amount,
        userInfoStore.precision
      );
      fishingBetsStore.setCashOutWin(
        currentFreeBet
          ? cashOutWin > currentFreeBet?.maxPayout
            ? currentFreeBet?.maxPayout
            : cashOutWin
          : cashOutWin
      );
      fishingBetsStore.setIsAllowCashOut(true);
    }
  }

  resetGameStoreAfterRoundFinished() {
    this.resetGameStore();
    fishingBetsStore.setOpenedFishes([]);
  }

  resetGameStoreAfterCashOut() {
    betHistoryStore.getLastBet();
    this.resetGameStore();
    fishingBetsStore.setIsAllowCashOut(false);
    fishingBetsStore.setIsBetsClosed(false);
    fishingBetsStore.setUserBet(null);
  }

  resetGameStore() {
    fishingBetsStore.setWinAmount(null);
    fishingBetsStore.setWinOdd(null);
    fishingBetsStore.setIsBetAccepted(false);
    fishingBetsStore.setCashOutWin(null);
    fishingBetsStore.setIsBetsClosed(true);
    fishingBetsStore.setIsAllowCashOut(false);
    fishingBetsStore.setUserBet(null);
    userInfoStore.getBalance();
  }

  countDown({ data }) {
    fishingBetsStore.setTime(data?.timerValue);
  }

  sendWorker(countdownData) {
    if (countdownData) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(countdownData);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }
}

export const fishingGameService = new FishingGameService();
